import { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, LinearProgress, Paper, Skeleton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import axios from 'axios'
import moment from 'moment'
import Icon from '../../../components/icon'
import apiUrl from '../../../configs/constant'
import MsiDatagrid from '../../../components/custom/MsiDatagridAuto'

type Period = { awal: string; akhir: string }
type Amounts = { total_tagihan: number; total_terbayar: number }
type ReportRow = Amounts & { id: number; nama: string; sales: string }
type DetailRow = Amounts & { customer_id: number; referensi: number; tanggal_transaksi: string; tanggal_pencairan: string; keterangan: string; is_aktif: number }
const initialPeriod = (): Period => ({ awal: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'), akhir: moment().endOf('month').format('YYYY-MM-DD') })
const money = (value: number) => `Rp ${new Intl.NumberFormat('id-ID', { maximumFractionDigits: 2 }).format(value)}`
const periodLabel = (period: Period) => `${moment(period.awal).format('DD/MM/YYYY')} - ${moment(period.akhir).format('DD/MM/YYYY')}`
const totals = (rows: Amounts[]) => rows.reduce((sum, row) => ({ tagihan: sum.tagihan + Number(row.total_tagihan ?? 0), terbayar: sum.terbayar + Number(row.total_terbayar ?? 0) }), { tagihan: 0, terbayar: 0 })
const normalizeAmounts = <T extends Amounts>(rows: T[]): T[] => rows.map(row => ({ ...row, total_tagihan: Number(row.total_tagihan ?? 0), total_terbayar: Number(row.total_terbayar ?? 0) }))
const numericColumns = ['total_tagihan', 'total_terbayar']
const dateColumns = ['tanggal_transaksi', 'tanggal_pencairan'].map(key => ({ key, renderCell: ({ value }: { value: string }) => value ? moment(value).format('DD/MM/YYYY') : '-' }))

function Summary({ rows, loading = false }: { rows: Amounts[]; loading?: boolean }) {
    const sum = totals(rows)
    return <Grid container spacing={2}>
        {[
            { label: 'Total tagihan', value: sum.tagihan, icon: 'mdi:file-document-outline', color: 'primary.main' },
            { label: 'Total terbayar', value: sum.terbayar, icon: 'mdi:check-circle-outline', color: 'success.main' },
            { label: 'Selisih tagihan', value: sum.tagihan - sum.terbayar, icon: 'mdi:wallet-outline', color: 'warning.main' }
        ].map(item => <Grid item xs={12} md={4} key={item.label}>
            <Paper variant='outlined' sx={{ p: 2.5, borderRadius: 3, height: '100%', boxSizing: 'border-box' }}>
                <Stack direction='row' spacing={1.5} alignItems='center'>
                    <Box sx={{ p: 1.25, display: 'flex', borderRadius: 2, color: item.color, bgcolor: 'action.hover' }}><Icon icon={item.icon} /></Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant='body2' color='text.secondary'>{item.label}</Typography>
                        {loading ? <Skeleton width={150} height={32} /> : <Typography sx={{ fontSize: { xs: 20, lg: 24 }, fontWeight: 750, overflowWrap: 'anywhere', fontVariantNumeric: 'tabular-nums' }}>{money(item.value)}</Typography>}
                    </Box>
                </Stack>
            </Paper>
        </Grid>)}
    </Grid>
}

export default function Index() {
    const userData = JSON.parse(localStorage.getItem('user') || '{}')
    const token = userData?.token
    const [filter, setFilter] = useState<Period>(initialPeriod)
    const [applied, setApplied] = useState<Period | null>(null)
    const [rows, setRows] = useState<ReportRow[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [selected, setSelected] = useState<ReportRow | null>(null)
    const [details, setDetails] = useState<DetailRow[]>([])
    const [detailLoading, setDetailLoading] = useState(false)
    const [detailError, setDetailError] = useState('')
    const reportRequest = useRef(0)
    const detailRequest = useRef(0)

    const getData = async (period: Period) => {
        if (!period.awal || !period.akhir || period.akhir < period.awal) {
            setError('Isi tanggal awal dan akhir dengan benar. Tanggal akhir tidak boleh mendahului tanggal awal.')
            return
        }
        const request = ++reportRequest.current
        setLoading(true)
        setError('')
        setSelected(null)
        ++detailRequest.current
        try {
            const response = await axios.get(`${apiUrl.apiUrl}/sales/laporan/pencairan`, { params: period, headers: { Authorization: `Bearer ${token}` } })
            if (!response.data.success) throw new Error(response.data.message || 'Gagal memuat laporan.')
            if (request === reportRequest.current) { setRows(normalizeAmounts(response.data.data)); setApplied(period) }
        } catch (err) {
            if (request === reportRequest.current) setError('Laporan gagal dimuat. Silakan coba lagi.')
        } finally {
            if (request === reportRequest.current) setLoading(false)
        }
    }
    useEffect(() => {
        getData(initialPeriod())
        // These refs are request counters; invalidate the latest requests on unmount.
        return () => { ++reportRequest.current; ++detailRequest.current }
        // Date changes are applied with the search button.
    }, [])

    const openDetail = async (row: ReportRow) => {
        if (!applied) return
        const customerId = Number(row.id)
        const request = ++detailRequest.current
        setSelected(row)
        setDetails([])
        setDetailError('')
        setDetailLoading(true)
        try {
            if (!Number.isSafeInteger(customerId) || customerId <= 0) throw new Error('ID customer tidak valid.')
            const response = await axios.get(`${apiUrl.apiUrl}/sales/laporan/pencairan/customer/${customerId}`, { params: applied, headers: { Authorization: `Bearer ${token}` } })
            if (!response.data.success) throw new Error(response.data.message || 'Gagal memuat detail.')
            if (Number(response.data.customer_id) !== customerId || !Array.isArray(response.data.data)
                || response.data.data.some((detail: DetailRow) => Number(detail.customer_id) !== customerId || detail.referensi == null)) {
                throw new Error('Respons detail tidak sesuai customer yang dipilih.')
            }
            if (request === detailRequest.current) setDetails(normalizeAmounts(response.data.data))
        } catch (err) {
            if (request === detailRequest.current) setDetailError('Detail pencairan gagal dimuat. Silakan coba lagi.')
        } finally {
            if (request === detailRequest.current) setDetailLoading(false)
        }
    }
    const closeDetail = () => { ++detailRequest.current; setSelected(null) }
    const downloadPdf = () => {
        if (!applied) return
        const params = new URLSearchParams({ ...applied, hrstaff_id: String(userData?.data?.hrstaff_id ?? userData?.data?.hr_staff_id ?? 0) })
        window.open(`${apiUrl.baseUrl}/marketing/laporan-sales/pencairan/pdf?${params}`, '_blank', 'noopener,noreferrer')
    }
    const sum = totals(rows)
    const percentage = sum.tagihan > 0 ? (sum.terbayar / sum.tagihan) * 100 : 0
    const filterChanged = applied && (filter.awal !== applied.awal || filter.akhir !== applied.akhir)

    return <Box className='content-block' sx={{ pb: 3 }}>
        <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                <Box>
                    <Typography variant='overline' color='primary.main' fontWeight={700}>SALES / LAPORAN</Typography>
                    <Typography variant='h4' sx={{ fontWeight: 750, fontSize: { xs: 26, md: 32 } }}>Laporan Pencairan</Typography>
                    <Typography color='text.secondary' sx={{ mt: 0.75 }}>Pantau tagihan dan realisasi pencairan setiap customer.</Typography>
                </Box>
                <Button variant='outlined' startIcon={<Icon icon='mdi:file-pdf-box' />} onClick={downloadPdf} disabled={loading || !applied}>Download PDF</Button>
            </Stack>
            <Paper variant='outlined' sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography fontWeight={700} sx={{ mb: 2 }}>Periode pencairan</Typography>
                <Box component='form' onSubmit={event => { event.preventDefault(); getData(filter) }}>
                    <Grid container spacing={2} alignItems='center'>
                        <Grid item xs={12} sm={6} md={4}><TextField fullWidth size='small' label='Tanggal awal' type='date' value={filter.awal} onChange={e => setFilter({ ...filter, awal: e.target.value })} InputLabelProps={{ shrink: true }} required /></Grid>
                        <Grid item xs={12} sm={6} md={4}><TextField fullWidth size='small' label='Tanggal akhir' type='date' value={filter.akhir} onChange={e => setFilter({ ...filter, akhir: e.target.value })} InputLabelProps={{ shrink: true }} inputProps={{ min: filter.awal }} required /></Grid>
                        <Grid item xs={12} md={4}><Button type='submit' variant='contained' disabled={loading} startIcon={<Icon icon='mdi:magnify' />} sx={{ minHeight: 40 }}>Tampilkan</Button></Grid>
                    </Grid>
                </Box>
                {filterChanged && <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 1.5 }}>Periode berubah. Klik Tampilkan untuk memperbarui laporan dan PDF.</Typography>}
            </Paper>
            {error && <Alert severity='error'>{error}{applied && ' Data yang ditampilkan masih dari pencarian terakhir yang berhasil.'}</Alert>}
            {(loading || applied) && <>
                <Summary rows={rows} loading={loading} />
                <Paper variant='outlined' sx={{ p: 2.5, borderRadius: 3 }}>
                    <Stack direction='row' justifyContent='space-between' spacing={2} sx={{ mb: 1 }}>
                        <Typography variant='body2' fontWeight={700}>Realisasi pembayaran</Typography>
                        <Typography variant='body2' fontWeight={700}>{loading ? 'Memuat...' : `${new Intl.NumberFormat('id-ID', { maximumFractionDigits: 1 }).format(percentage)}%`}</Typography>
                    </Stack>
                    <LinearProgress variant={loading ? 'indeterminate' : 'determinate'} value={Math.max(0, Math.min(100, percentage))} color='success' sx={{ height: 8, borderRadius: 4 }} />
                    <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 1 }}>Total terbayar dibandingkan total tagihan seluruh customer dalam periode laporan.</Typography>
                </Paper>
            </>}
            <Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' spacing={1} sx={{ mb: 1.5 }}>
                    <Typography variant='body2' color='text.secondary'>Klik ikon mata untuk melihat daftar transaksi pencairan.</Typography>
                    {applied && <Chip size='small' variant='outlined' label={periodLabel(applied)} />}
                </Stack>
                <MsiDatagrid array={rows} loading={loading} title='Pencairan per customer' searchPlaceholder='Cari customer atau sales...' groupPanel={false} hiddenColumns={['id']} convertNumeric={numericColumns}
                    actions={[{ key: 'detail', caption: 'Detail', align: 'center', minWidth: 75, renderCell: ({ row }) =>
                        <Tooltip title='Lihat detail pencairan'><IconButton color='primary' size='small' aria-label={`Lihat detail pencairan ${row.nama}`} onClick={() => openDetail(row)}><Icon icon='mdi:eye-outline' /></IconButton></Tooltip>
                    }]} />
            </Box>
        </Stack>
        <Dialog open={Boolean(selected)} onClose={closeDetail} fullWidth maxWidth='lg' aria-labelledby='pencairan-detail-title' PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle id='pencairan-detail-title' sx={{ pr: 7 }}>Detail pencairan
                <IconButton onClick={closeDetail} aria-label='Tutup detail pencairan' sx={{ position: 'absolute', right: 12, top: 12 }}><Icon icon='mdi:close' /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    <Box>
                        <Typography variant='h6' fontWeight={700}>{selected?.nama}</Typography>
                        <Typography variant='body2' color='text.secondary'>Sales: {selected?.sales || '-'}{applied && ` | Periode ${periodLabel(applied)}`}</Typography>
                    </Box>
                    {detailError ? <Alert severity='error' action={<Button color='inherit' size='small' onClick={() => selected && openDetail(selected)}>Coba lagi</Button>}>{detailError}</Alert> : <>
                        <Summary rows={details} loading={detailLoading} />
                        <MsiDatagrid key={selected?.id} array={details} loading={detailLoading} title={`Daftar transaksi pencairan - ${selected?.nama || ''}`} searchPlaceholder='Cari referensi atau keterangan...' groupPanel={false} convertNumeric={numericColumns} customRender={dateColumns} hiddenColumns={['is_aktif', 'customer_id']}
                            rowStyleRules={[{ field: 'is_aktif', operator: '==', value: 0, color: '#fff1f0' }]} />
                        {!detailLoading && details.some(row => Number(row.is_aktif) === 0) && <Typography variant='caption' color='text.secondary'>Baris berwarna merah muda menandakan transaksi tidak aktif.</Typography>}
                    </>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}><Button onClick={closeDetail} variant='outlined'>Tutup</Button></DialogActions>
        </Dialog>
    </Box>
}
